/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from '../../../state'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import sportNinjaLogoDark from './sportninja_dark.svg'
import sportNinjaLogoLight from './sportninja_light.svg'
import canlanLogo from '../../RegistrationPayment/canlan_logo.svg'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { PasswordOnly } from '../../../components/Form/ConfirmPassword'
import { Link, useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import { linkStyle } from '../../../components/css'
import { ForgotText } from '../../../components/AuthWrap/css'
import Form from '../../../components/Form'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import { t } from '@sportninja/common/i18n'
import TextModal from '../../../components/Modal/TextModal'
import ResendVerifyEmail from '../../../components/ResendVerifyEmail'
import { useCallback, useEffect, useMemo } from 'react'

import hockeyLight from './hockey-light.png'
import hockeyDark from './hockey-dark.png'
import soccerLight from './soccer-light.png'
import soccerDark from './soccer-dark.png'
import { EventCard } from './ConfirmProfile'
import soccerDefaultBg from './soccer_default_bg.png'
import hockeyDefaultBg from './hockey_default_bg.png'

const onError = (form, error) => {
  let message
  switch (error.status) {
    case 401:
      message = t('errors:invalidLogin')
      break
    case 403:
      message = (
        <>
          <p>{t('Auth:youNeedToVerify')}!</p>
          <TextModal
            title={t('Auth:resendEmail')}
            link={(onClick) => (
              <p css={linkStyle} onClick={onClick}>
                {t('Auth:resendVerificationEmail')}?
              </p>
            )}
          >
            <ResendVerifyEmail email={form.email} />
          </TextModal>
        </>
      )
      break
    default:
      message = error.message
  }

  return { message }
}

const form = [
  [
    {
      name: 'email',
      label: 'Email',
      autoComplete: 'email',
      type: 'email',
      autoCorrect: 'off',
      autoCapitalize: 'none',
      required: true,
      defaultValue: '',
    },
  ],
  [{ component: PasswordOnly }],
]

export const EventDetails = ({ label, value }) => (
  <p
    css={css`
      font-size: 16px;
      margin: 8px 0;

      @media (max-width: 768px) {
        font-size: 14px;
        line-height: 16px;
      }
    `}
  >
    <span
      css={css`
        font-weight: 700;
      `}
    >
      {label + ': '}
    </span>
    {value}
  </p>
)

export const Login = observer(({ login }) => {
  const {
    appStore: { setDarkMode, setLightMode },
    registrationInvite: {
      state: { eventInfo, error, sports },
    },
  } = useMobxStore()

  const history = useHistory()

  const defaultBackgroundImage = useMemo(() => {
    const sport = sports.find((sport) => sport.id === eventInfo?.sport_id)

    if (sport?.name === 'soccer') {
      return soccerDefaultBg
    }

    return hockeyDefaultBg
  }, [eventInfo?.sport_id, sports])

  const apiBackgroundImage = useMemo(() => {
    return eventInfo?.registration?.image?.full_path || defaultBackgroundImage
  }, [eventInfo?.registration?.image, defaultBackgroundImage])

  const isDarkMode = useMemo(
    () => eventInfo?.registration?.dark_theme,
    [eventInfo?.registration?.dark_theme]
  )

  useEffect(() => {
    if (!isDarkMode) {
      setLightMode()
    } else {
      setDarkMode()
    }
  }, [isDarkMode])

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
      `}
    >
      <div
        css={css`
          display: flex;
          height: 100vh;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          flex: 2;
          background-color: ${isDarkMode ? '#12161C' : colors.WHITE};

          > * {
            color: ${isDarkMode ? '#fff' : '#000'};
          }
        `}
      >
        <div
          css={css`
            background-image: url(${apiBackgroundImage});
            background-position: center;
            background-size: cover;
            background-repeat: no-repeat;
            width: calc(100vw - 520px);
            height: 400px;
          `}
        />
        <div
          css={css`
            padding: 40px;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            flex-shrink: 0;
            width: 700px;
            display: flex;
            border-radius: 16px 16px 0px 0px;
            background-color: ${isDarkMode ? '#21252A' : colors.WHITE};
            box-shadow: 0px 10px 18px -2px rgba(16, 25, 40, 0.07);
            margin-top: -50px;
            height: calc(100vh - 260px);
          `}
        >
          <h1
            css={css`
              font-family: Rift;
              font-size: 40px;
              font-style: normal;
              font-weight: 700;
              letter-spacing: 0.6px;
              text-transform: uppercase;
              color: ${isDarkMode ? colors.WHITE : colors.HEADER};
            `}
          >
            Welcome to registration!
          </h1>
          <div
            css={css`
              width: 100%;
              margin-top: -20px;
            `}
          >
            <EventCard eventInfo={eventInfo} isDarkMode={isDarkMode} />
          </div>
          {/* <EventDetails
            label='Organization'
            value={eventInfo?.registration?.organization_name}
          />
          <EventDetails
            label='Dates'
            value={`${dayjs(eventInfo?.registration?.starts_at).format(
              'MMM D'
            )} - ${dayjs(eventInfo?.registration?.ends_at).format(
              'MMM D, YYYY'
            )} `}
          />
          {eventInfo?.registration?.location && (
            <EventDetails label='Location' value={eventInfo?.location} />
          )} */}
          <p
            css={css`
              text-align: center;
              font-size: 18px;
              font-style: normal;
              font-weight: 500;
              line-height: 120%; /* 21.6px */
            `}
          >
            Let’s get started with registration.
            <br />
            Please login or create a new account.
          </p>
          <div
            css={css`
              margin-top: 16px;
              display: flex;
              align-items: center;
            `}
          >
            <p>Powered by</p>
            <img
              src={isDarkMode ? sportNinjaLogoDark : sportNinjaLogoLight}
              css={css`
                margin-left: 8px;
                width: 84px;
                height: 18px;
              `}
            />
          </div>
        </div>
      </div>

      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          box-shadow: -15px 0px 18px 0px rgba(0, 0, 0, 0.75);
          height: 100vh;
          padding: 40px;
          z-index: 1;
          background: no-repeat fixed linear-gradient(#282e38, #181a1d);

          > * {
            z-index: 1;
          }
        `}
      >
        <img
          src={isCanlan ? canlanLogo : sportNinjaLogoDark}
          css={css`
            width: 20em;
            height: 5em;
            align-self: center;
          `}
        />
        <p
          css={css`
            margin-top: 20%;
            font-size: 24px;
            font-weight: 700;
          `}
        >
          Hi, Welcome back! 👋
        </p>

        {error && (
          <p
            css={css`
              margin-top: 5%;
              color: ${colors.ERROR};
            `}
          >
            {JSON.parse(error).statusCode === 401
              ? t('errors:invalidLogin')
              : JSON.parse(error).message}
          </p>
        )}

        <div
          css={css`
            margin-top: 20%;
            padding-right: 40px;
            flex-wrap: wrap;
          `}
        >
          <Form
            css={css`
              flex: initial;
            `}
            form={form}
            s
            hideCancel
            onError={onError}
            onSubmit={({ email, password }) => {
              login({ email, password })
            }}
            submitButton={(props) => {
              return (
                <button
                  disabled={props?.disabled}
                  css={css`
                    cursor: ${props?.disabled ? 'not-allowed' : 'pointer'};
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    width: 100%;
                    height: 40px;
                    border-radius: 8px;
                    background-color: ${props?.disabled
                      ? colors.SLATE_GRAY
                      : colors.DEFAULT_FLAIR};
                    color: ${colors.WHITE};
                    font-size: 16px;
                    font-weight: 700;
                    font-family: ${isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                      : 'Rift, Arial, Helvetica, sans-serif'};
                    transition: all 0.2s ease-in-out;

                    &:hover {
                      background-color: ${props?.disabled
                        ? colors.SLATE_GRAY
                        : colors.DEFAULT_FLAIR_HOVER};
                    }
                  `}
                >
                  LOGIN
                </button>
              )
            }}
            submitText={'Login'}
          >
            <ForgotText as={Link} to={ROUTES.FORGOT_PASSWORD}>
              <span css={linkStyle}>Forgot password</span>
            </ForgotText>
          </Form>
          <div
            css={css`
              margin-top: 16px;
              padding-top: 16px;
              display: flex;
              border-top: 1px solid ${colors.SOFT_STEEL};
              justify-content: center;
              align-items: center;
            `}
          >
            <p
              css={css`
                font-size: 16px;
              `}
            >
              Don’t have an account?{' '}
              <button
                onClick={() =>
                  history.push(
                    `${ROUTES.REGISTER}?isRegistration=true&registrationId=${eventInfo?.registration?.schedule_id}`
                  )
                }
                css={css`
                  color: ${colors.DEFAULT_FLAIR};
                  font-size: 16px;
                `}
              >
                Sign up
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
})
